import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { css, ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import { faSpinner } from '@fortawesome/pro-regular-svg-icons/faSpinner';
import Icon from '@veeva/corgix/icon';
import Link from '../components/Link';
import PageContainer from '../components/PageContainer';
import ResponseStatus from '../constants/ResponseStatus';
import { AuthenticatedPaths } from '../constants/Paths';
import ResponseErrorTypes from '../constants/ResponseErrorTypes';
import useMessageService from '../services/i18n/useMessageService';
import { isMobile } from '../services/DeviceInformation';
import { navigateToErrorPage, getFirstError } from '../services/errorUtils';
import { fetchPendingRegistrations } from '../veevaIdApi';
import Loading from '../components/Loading';

const VaultsPage = () => {
    const [
        [
            title = 'Pending Vault Invites',
            description = 'You have been added to the following Vault Systems. Click on a Vault to get started:',
        ],
    ] = useMessageService(['vaults_page.title', 'vaults_page.description']);

    const [pendingRegistrations, setPendingRegistrations] = useState(undefined);
    const [loading, setLoading] = useState(true);
    const history = useHistory();

    useEffect(() => {
        (async () => {
            const result = await fetchPendingRegistrations();
            if (result.responseStatus === ResponseStatus.SUCCESS) {
                // When the server returns a null `data` (falsy)
                // that means there are no pending invites
                // and we should redirect the user back to /home.
                if (!result?.data) {
                    history.replace(AuthenticatedPaths.HOME);
                }

                setPendingRegistrations(result?.data);
                setLoading(false);
            } else if (
                result.responseStatus === ResponseStatus.FAILURE ||
                result.responseStatus === ResponseStatus.UI_NETWORK_ERROR
            ) {
                const error = getFirstError(result);
                navigateToErrorPage(
                    history,
                    error ?? { type: ResponseErrorTypes.VEEVAID_UNEXPECTED_ERROR },
                );
            }
        })();
    }, [history]);

    return (
        <PageContainer title={title}>
            <EmotionThemeProvider
                theme={{
                    linkFontSize: '1.5rem',
                }}
            >
                <div>
                    <span
                        css={[
                            css`
                                display: flex;
                                font-size: 1.5rem;
                                margin-bottom: 1.6667rem;
                                justify-content: center;
                            `,
                            pendingRegistrations &&
                                css`
                                    padding: 0 3.333rem;
                                `,
                        ]}
                    >
                        {description}
                    </span>
                    {loading &&
                        (isMobile() ? (
                            <Loading backgroundColorDisabled />
                        ) : (
                            <Icon spin type={faSpinner} color="#555555" size="xl" />
                        ))}
                    {!loading && (
                        <div
                            css={[
                                css`
                                    display: flex;
                                    flex-direction: column;
                                    font-size: 1.5rem;
                                    gap: 0.6667rem;

                                    & > span:first-of-type {
                                        margin-top: 0.6667rem;
                                    }
                                `,
                                !isMobile() &&
                                    css`
                                        overflow-y: auto;
                                        max-height: 294px;
                                        width: 500px;
                                    `,
                            ]}
                            className="vaultsPage"
                        >
                            {pendingRegistrations.map((registration) => (
                                <span
                                    key={registration.name}
                                    css={css`
                                        padding: 0 3.333rem;
                                    `}
                                >
                                    <Link href={registration.url}>{registration.name}</Link>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </EmotionThemeProvider>
        </PageContainer>
    );
};

export default VaultsPage;
